import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  MessageCircle,
  Send,
  Search,
  RefreshCcw,
  QrCode,
  Wifi,
  WifiOff,
  Users,
  Megaphone,
  FileText,
  Phone,
  User,
  Check,
  CheckCheck,
  Clock,
  Loader2,
  Settings,
  Plus,
  Paperclip,
  Image as ImageIcon,
  File,
  X,
  Sparkles,
  ChevronRight,
  UserPlus,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

// API URL helper
const getApiUrl = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  const isPreview = hostname.includes('preview.emergentagent.com');
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  return isPreview ? '' : envUrl;
};
const API_URL = getApiUrl();

const WhatsAppPage = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('chats');
  
  // Connection state
  const [connectionState, setConnectionState] = useState('loading'); // Start with loading state
  const [qrCode, setQrCode] = useState(null);
  const [waUser, setWaUser] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true); // Track initial status load
  
  // Chat state
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Broadcast state
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastFilter, setBroadcastFilter] = useState('all');
  const [broadcastLists, setBroadcastLists] = useState({});
  const [broadcastClients, setBroadcastClients] = useState([]);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', message: '', category: 'general' });
  
  // New chat state (for starting chat with mapped clients)
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [mappedClients, setMappedClients] = useState([]);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [loadingMappedClients, setLoadingMappedClients] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Fetch connection status
  const fetchStatus = useCallback(async (isInitial = false) => {
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        console.log('[WhatsApp] Status fetched:', data.state);
        setConnectionState(data.state || 'disconnected');
        if (data.qrCode) setQrCode(data.qrCode);
        if (data.user) setWaUser(data.user);
      } else {
        console.error('[WhatsApp] Status fetch failed:', res.status);
        if (isInitial) setConnectionState('disconnected');
      }
    } catch (err) {
      console.error('[WhatsApp] Error fetching status:', err);
      if (isInitial) setConnectionState('disconnected');
    } finally {
      if (isInitial) setStatusLoading(false);
    }
  }, [token]);
  
  // Fetch chats - with data comparison to prevent unnecessary re-renders
  const fetchChats = useCallback(async (forceUpdate = false) => {
    if (!forceUpdate) {
      // Don't show loading for background polling
    } else {
      setLoadingChats(true);
    }
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/chats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Only update if data actually changed
        setChats(prevChats => {
          const prevJson = JSON.stringify(prevChats.map(c => ({ phone: c.phone, lastMessage: c.lastMessage?.text, unread: c.unreadCount })));
          const newJson = JSON.stringify(data.map(c => ({ phone: c.phone, lastMessage: c.lastMessage?.text, unread: c.unreadCount })));
          if (prevJson !== newJson) {
            return data;
          }
          return prevChats;
        });
      }
    } catch (err) {
      console.error('Error fetching chats:', err);
    } finally {
      setLoadingChats(false);
    }
  }, [token]);
  
  // Fetch messages for a chat - with data comparison
  const fetchMessages = useCallback(async (phone, forceUpdate = false) => {
    if (forceUpdate) {
      setLoadingMessages(true);
    }
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/chat/${phone}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Only update if data actually changed
        setMessages(prevMessages => {
          if (prevMessages.length !== data.length) {
            return data;
          }
          // Check if last message is different
          const lastPrev = prevMessages[prevMessages.length - 1];
          const lastNew = data[data.length - 1];
          if (lastPrev?.messageId !== lastNew?.messageId || lastPrev?.text !== lastNew?.text) {
            return data;
          }
          return prevMessages;
        });
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, [token]);
  
  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, [token]);
  
  // Fetch broadcast lists
  const fetchBroadcastLists = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/broadcast-lists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBroadcastLists(data);
      }
    } catch (err) {
      console.error('Error fetching broadcast lists:', err);
    }
  }, [token]);
  
  // Fetch mapped clients for new chat
  const fetchMappedClients = useCallback(async (search = '') => {
    setLoadingMappedClients(true);
    try {
      const url = search 
        ? `${API_URL}/api/whatsapp/mapped-clients?search=${encodeURIComponent(search)}`
        : `${API_URL}/api/whatsapp/mapped-clients`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMappedClients(data);
      }
    } catch (err) {
      console.error('Error fetching mapped clients:', err);
    } finally {
      setLoadingMappedClients(false);
    }
  }, [token]);
  
  // Initial load
  useEffect(() => {
    if (token) {
      // Fetch status first with initial flag
      fetchStatus(true);
      fetchChats(true); // Force update on initial load
      fetchTemplates();
      fetchBroadcastLists();
    }
  }, [token, fetchStatus, fetchChats, fetchTemplates, fetchBroadcastLists]);
  
  // Polling for real-time updates (since WebSocket to Node.js service won't work from browser)
  useEffect(() => {
    if (!token || connectionState !== 'connected') return;
    
    // Poll for new chats every 10 seconds (reduced frequency to prevent flickering)
    const chatPollInterval = setInterval(() => {
      fetchChats(false); // false = background poll, no loading state
    }, 10000);
    
    // Poll for status every 30 seconds
    const statusPollInterval = setInterval(() => {
      fetchStatus();
    }, 30000);
    
    return () => {
      clearInterval(chatPollInterval);
      clearInterval(statusPollInterval);
    };
  }, [token, connectionState, fetchChats, fetchStatus]);
  
  // Poll for messages in selected chat
  useEffect(() => {
    if (!token || !selectedChat || connectionState !== 'connected') return;
    
    // Poll for new messages every 5 seconds when a chat is selected
    const messagePollInterval = setInterval(() => {
      fetchMessages(selectedChat.phone, false); // false = background poll
    }, 5000);
    
    return () => {
      clearInterval(messagePollInterval);
    };
  }, [token, selectedChat, connectionState, fetchMessages]);
  
  // Poll for QR code when connecting or loading
  useEffect(() => {
    if (!token) return;
    // Poll while loading or connecting
    if (connectionState !== 'connecting' && connectionState !== 'qr_ready' && connectionState !== 'loading') return;
    
    const qrPollInterval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/whatsapp/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          console.log('[WhatsApp] Poll status:', data.state);
          setConnectionState(data.state || 'disconnected');
          if (data.qrCode) {
            setQrCode(data.qrCode);
          }
          if (data.user) {
            setWaUser(data.user);
          }
          if (data.state === 'connected') {
            setQrCode(null);
            fetchChats(true);
          }
        }
      } catch (err) {
        console.error('Error polling status:', err);
      }
    }, 2000);
    
    return () => {
      clearInterval(qrPollInterval);
    };
  }, [token, connectionState, fetchChats]);
  
  // Connect to WhatsApp
  const handleConnect = async () => {
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/connect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Connecting to WhatsApp...');
        setConnectionState('connecting');
      }
    } catch (err) {
      toast.error('Failed to connect');
    }
  };
  
  // Disconnect from WhatsApp
  const handleDisconnect = async () => {
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Disconnected from WhatsApp');
        setConnectionState('disconnected');
        setQrCode(null);
        setWaUser(null);
      }
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };
  
  // Reset WhatsApp session (fixes Bad MAC errors)
  const handleResetSession = async () => {
    if (!confirm('Reset WhatsApp session? You will need to scan the QR code again.')) {
      return;
    }
    
    try {
      toast.info('Resetting session...');
      const res = await fetch(`${API_URL}/api/whatsapp/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Session reset. Please scan QR code to reconnect.');
        setConnectionState('disconnected');
        setQrCode(null);
        setWaUser(null);
        setChats([]);
        setMessages([]);
        setSelectedChat(null);
        
        // Wait a moment then fetch status to get new QR
        setTimeout(() => {
          fetchStatus();
        }, 2000);
      } else {
        toast.error(data.error || 'Failed to reset session');
      }
    } catch (err) {
      toast.error('Failed to reset session');
    }
  };
  
  // Select a chat
  const handleSelectChat = async (chat) => {
    setSelectedChat(chat);
    await fetchMessages(chat.phone);
    
    // Mark as read
    await fetch(`${API_URL}/api/whatsapp/read/${chat.phone}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  };
  
  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    
    setSendingMessage(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phone: selectedChat.phone,
          message: newMessage,
          type: 'text',
        }),
      });
      
      if (res.ok) {
        setNewMessage('');
        // Message will be added via WebSocket
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to send message');
      }
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };
  
  // Send broadcast
  const handleSendBroadcast = async () => {
    if (!broadcastMessage.trim() || broadcastClients.length === 0) return;
    
    setSendingBroadcast(true);
    try {
      const phones = broadcastClients.map(c => c.phone);
      
      const res = await fetch(`${API_URL}/api/whatsapp/broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phones,
          message: broadcastMessage,
          type: 'text',
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        const successCount = data.results?.filter(r => r.success).length || 0;
        toast.success(`Broadcast sent to ${successCount}/${phones.length} recipients`);
        setShowBroadcastDialog(false);
        setBroadcastMessage('');
      } else {
        toast.error('Failed to send broadcast');
      }
    } catch (err) {
      toast.error('Failed to send broadcast');
    } finally {
      setSendingBroadcast(false);
    }
  };
  
  // Load clients for broadcast
  const loadBroadcastClients = async (filter) => {
    try {
      let url = `${API_URL}/api/whatsapp/clients-for-broadcast`;
      const params = new URLSearchParams();
      
      if (filter && filter !== 'all') {
        if (filter.startsWith('tier_')) {
          params.append('tier', filter.replace('tier_', ''));
        } else if (filter.startsWith('rm_')) {
          params.append('rm_id', filter.replace('rm_', ''));
        }
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setBroadcastClients(data);
      }
    } catch (err) {
      console.error('Error loading broadcast clients:', err);
    }
  };
  
  // Save template
  const handleSaveTemplate = async () => {
    if (!newTemplate.name || !newTemplate.message) return;
    
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newTemplate),
      });
      
      if (res.ok) {
        toast.success('Template saved');
        setShowTemplateDialog(false);
        setNewTemplate({ name: '', message: '', category: 'general' });
        fetchTemplates();
      }
    } catch (err) {
      toast.error('Failed to save template');
    }
  };
  
  // Use template
  const handleUseTemplate = (template) => {
    setNewMessage(template.message);
  };
  
  // Filter chats by search
  const filteredChats = chats.filter(chat => 
    chat.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.phone?.includes(searchQuery) ||
    chat.client?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Render connection status
  const renderConnectionStatus = () => {
    const statusConfig = {
      loading: { icon: Loader2, color: 'text-slate-400', bg: 'bg-slate-500/10', label: 'Loading...', animate: true },
      disconnected: { icon: WifiOff, color: 'text-slate-400', bg: 'bg-slate-500/10', label: 'Disconnected' },
      connecting: { icon: Loader2, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Connecting...', animate: true },
      qr_ready: { icon: QrCode, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Scan QR Code' },
      connected: { icon: Wifi, color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Connected' },
      error: { icon: WifiOff, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Error' },
    };
    
    const config = statusConfig[connectionState] || statusConfig.loading;
    const Icon = config.icon;
    
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg}`}>
        <Icon className={`w-4 h-4 ${config.color} ${config.animate ? 'animate-spin' : ''}`} />
        <span className={`text-sm ${config.color}`}>{config.label}</span>
      </div>
    );
  };
  
  // Render message status icon
  const renderMessageStatus = (msg) => {
    if (!msg.fromMe) return null;
    
    switch (msg.status) {
      case 'sent':
        return <Check className="w-3 h-3 text-slate-400" />;
      case 'delivered':
        return <CheckCheck className="w-3 h-3 text-slate-400" />;
      case 'read':
        return <CheckCheck className="w-3 h-3 text-blue-400" />;
      default:
        return <Clock className="w-3 h-3 text-slate-400" />;
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-emerald-500" />
            WhatsApp
          </h1>
          <p className="text-slate-400 mt-1">
            Communicate with clients via WhatsApp
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {renderConnectionStatus()}
          
          {connectionState === 'disconnected' && (
            <Button
              onClick={handleConnect}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="connect-whatsapp-btn"
            >
              <Wifi className="w-4 h-4 mr-2" />
              Connect
            </Button>
          )}
          
          {connectionState === 'connected' && user?.role === 'principal' && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleDisconnect}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="disconnect-whatsapp-btn"
              >
                <WifiOff className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
              <Button
                variant="outline"
                onClick={handleResetSession}
                className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                data-testid="reset-session-btn"
                title="Reset session if messages aren't being received"
              >
                <RefreshCcw className="w-4 h-4 mr-2" />
                Reset Session
              </Button>
            </div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchStatus(); fetchChats(true); }}
            className="border-white/10"
            data-testid="refresh-btn"
          >
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Session Error Alert */}
      {connectionState === 'connected' && (
        <div id="session-error-alert" className="hidden mb-4">
          <Card className="border-yellow-500/30 bg-yellow-500/5">
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
                <div className="flex-1">
                  <p className="text-yellow-400 font-medium">Session Issues Detected</p>
                  <p className="text-slate-400 text-sm">If messages are not being sent/received, try resetting the session.</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleResetSession}
                  className="border-yellow-500/50 text-yellow-400"
                >
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* QR Code Dialog */}
      {(connectionState === 'qr_ready' || connectionState === 'connecting') && qrCode && (
        <Card className="card-dark border-blue-500/30 bg-blue-500/5">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <QrCode className="w-12 h-12 text-blue-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Scan QR Code</h3>
              <p className="text-slate-400 mb-6">
                Open WhatsApp on your phone → Settings → Linked Devices → Link a Device
              </p>
              <div className="bg-white p-4 rounded-lg">
                <img
                  src={qrCode}
                  alt="WhatsApp QR Code"
                  className="w-64 h-64"
                />
              </div>
              <p className="text-sm text-slate-500 mt-4">
                QR code expires in 60 seconds. Click refresh if expired.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Main Content */}
      {connectionState === 'connected' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat List */}
          <Card className="card-dark border-white/5 lg:col-span-1">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg">Chats</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      fetchMappedClients();
                      setShowNewChatDialog(true);
                    }}
                    className="border-white/10"
                    data-testid="new-chat-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    New
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      loadBroadcastClients('all');
                      setShowBroadcastDialog(true);
                    }}
                    className="border-white/10"
                    data-testid="broadcast-btn"
                  >
                    <Megaphone className="w-4 h-4 mr-1" />
                    Broadcast
                  </Button>
                </div>
              </div>
              <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Only shows messages from mapped clients
              </p>
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search chats..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-slate-800 border-white/10"
                  data-testid="search-chats"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                {loadingChats ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                  </div>
                ) : filteredChats.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No chats from mapped clients</p>
                    <p className="text-xs mt-1">Start a new chat with a client</p>
                  </div>
                ) : (
                  filteredChats.map((chat) => (
                    <div
                      key={chat.phone}
                      onClick={() => handleSelectChat(chat)}
                      className={`flex items-center gap-3 p-4 cursor-pointer transition-colors border-b border-white/5 ${
                        selectedChat?.phone === chat.phone
                          ? 'bg-[#C5A059]/10'
                          : 'hover:bg-white/5'
                      }`}
                      data-testid={`chat-${chat.phone}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-white font-medium truncate">
                            {chat.client?.name || chat.name || chat.phone}
                          </p>
                          {chat.unread > 0 && (
                            <Badge className="bg-emerald-500 text-white text-xs">
                              {chat.unread}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 truncate">
                          {chat.lastMessage}
                        </p>
                        {chat.client && (
                          <p className="text-xs text-[#C5A059] mt-0.5">
                            {chat.client.client_code} • {chat.client.tier || 'Client'}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </ScrollArea>
            </CardContent>
          </Card>
          
          {/* Chat Window */}
          <Card className="card-dark border-white/5 lg:col-span-2">
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <CardHeader className="border-b border-white/5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-medium">
                        {selectedChat.client?.name || selectedChat.name || selectedChat.phone}
                      </h3>
                      <p className="text-sm text-slate-400 flex items-center gap-2">
                        <Phone className="w-3 h-3" />
                        {selectedChat.phone}
                        {selectedChat.client && (
                          <>
                            <span>•</span>
                            <span className="text-[#C5A059]">{selectedChat.client.client_code}</span>
                          </>
                        )}
                      </p>
                    </div>
                    {selectedChat.client && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          const res = await fetch(`${API_URL}/api/whatsapp/send-portfolio/${selectedChat.client.client_code}`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          if (res.ok) {
                            toast.success('Portfolio summary sent');
                          } else {
                            toast.error('Failed to send portfolio');
                          }
                        }}
                        className="border-[#C5A059]/50 text-[#C5A059] hover:bg-[#C5A059]/10"
                        data-testid="send-portfolio-btn"
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        Send Portfolio
                      </Button>
                    )}
                  </div>
                </CardHeader>
                
                {/* Messages */}
                <CardContent className="p-0">
                  <ScrollArea className="h-[350px] p-4">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="text-center text-slate-400 py-8">
                        No messages yet
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((msg, idx) => (
                          <div
                            key={msg.messageId || idx}
                            className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-lg px-4 py-2 ${
                                msg.fromMe
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-slate-700 text-white'
                              }`}
                            >
                              <p className="whitespace-pre-wrap">{msg.text}</p>
                              <div className={`flex items-center gap-1 mt-1 ${msg.fromMe ? 'justify-end' : ''}`}>
                                <span className="text-xs opacity-70">
                                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {renderMessageStatus(msg)}
                              </div>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>
                  
                  {/* Quick Templates */}
                  {templates.length > 0 && (
                    <div className="border-t border-white/5 p-2">
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {templates.slice(0, 5).map((template, idx) => (
                          <Button
                            key={idx}
                            size="sm"
                            variant="outline"
                            onClick={() => handleUseTemplate(template)}
                            className="border-white/10 whitespace-nowrap"
                          >
                            {template.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Message Input */}
                  <div className="border-t border-white/5 p-4">
                    <div className="flex gap-3">
                      <Input
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                        className="flex-1 bg-slate-800 border-white/10"
                        disabled={sendingMessage}
                        data-testid="message-input"
                      />
                      <Button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sendingMessage}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </>
            ) : (
              <CardContent className="flex flex-col items-center justify-center h-[500px] text-center">
                <MessageCircle className="w-16 h-16 text-slate-600 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Select a Chat</h3>
                <p className="text-slate-400">
                  Choose a conversation from the list to start messaging
                </p>
              </CardContent>
            )}
          </Card>
        </div>
      )}
      
      {/* Loading State - also show for 'connecting' state */}
      {(connectionState === 'loading' || connectionState === 'connecting' || statusLoading) && (
        <Card className="card-dark border-white/5">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Loader2 className="w-16 h-16 text-[#C5A059] mb-6 animate-spin" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {connectionState === 'connecting' ? 'Connecting to WhatsApp...' : 'Loading WhatsApp...'}
            </h3>
            <p className="text-slate-400">
              {connectionState === 'connecting' ? 'Please wait while we establish the connection' : 'Checking connection status'}
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Not Connected State */}
      {(connectionState === 'disconnected' || connectionState === 'error') && !statusLoading && connectionState !== 'connecting' && (
        <Card className="card-dark border-white/5">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <MessageCircle className="w-20 h-20 text-slate-600 mb-6" />
            <h3 className="text-2xl font-semibold text-white mb-3">Connect WhatsApp</h3>
            <p className="text-slate-400 mb-6 max-w-md">
              Connect your WhatsApp Business account to start communicating with clients directly from the CRM
            </p>
            {connectionState === 'error' && (
              <p className="text-amber-400 text-sm mb-4">
                Connection failed. Please ensure your network allows WhatsApp connections.
              </p>
            )}
            <Button
              onClick={handleConnect}
              className="bg-emerald-600 hover:bg-emerald-700"
              size="lg"
            >
              <QrCode className="w-5 h-5 mr-2" />
              Connect WhatsApp
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Broadcast Dialog */}
      <Dialog open={showBroadcastDialog} onOpenChange={setShowBroadcastDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#C5A059]" />
              Broadcast Message
            </DialogTitle>
            <DialogDescription>
              Send a message to multiple clients at once
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Select Recipients</label>
              <Select
                value={broadcastFilter}
                onValueChange={(value) => {
                  setBroadcastFilter(value);
                  loadBroadcastClients(value);
                }}
              >
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue placeholder="Select filter" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  <SelectItem value="all">All Clients ({broadcastLists.total_with_phone || 0})</SelectItem>
                  {Object.entries(broadcastLists.by_tier || {}).map(([tier, count]) => (
                    <SelectItem key={tier} value={`tier_${tier}`}>
                      {tier} Tier ({count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                {broadcastClients.length} recipients selected
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Message</label>
              <Textarea
                placeholder="Type your broadcast message..."
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                className="bg-slate-800 border-white/10 min-h-[120px]"
                data-testid="broadcast-message"
              />
            </div>
            
            {templates.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm text-slate-300">Quick Templates</label>
                <div className="flex flex-wrap gap-2">
                  {templates.map((template, idx) => (
                    <Button
                      key={idx}
                      size="sm"
                      variant="outline"
                      onClick={() => setBroadcastMessage(template.message)}
                      className="border-white/10"
                    >
                      {template.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowBroadcastDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendBroadcast}
              disabled={!broadcastMessage.trim() || broadcastClients.length === 0 || sendingBroadcast}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="send-broadcast-btn"
            >
              {sendingBroadcast ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send to {broadcastClients.length} recipients
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Create Template</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Template Name</label>
              <Input
                placeholder="e.g., Portfolio Update"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                className="bg-slate-800 border-white/10"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Message</label>
              <Textarea
                placeholder="Template message..."
                value={newTemplate.message}
                onChange={(e) => setNewTemplate({ ...newTemplate, message: e.target.value })}
                className="bg-slate-800 border-white/10 min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTemplate}
              className="bg-[#C5A059] hover:bg-[#b4914e]"
            >
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* New Chat Dialog - Select from Mapped Clients */}
      <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-400" />
              Start New Chat
            </DialogTitle>
            <DialogDescription>
              Select a client from your CRM to start a WhatsApp conversation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, code, or phone..."
                value={clientSearchQuery}
                onChange={(e) => {
                  setClientSearchQuery(e.target.value);
                  fetchMappedClients(e.target.value);
                }}
                className="pl-9 bg-slate-800 border-white/10"
                data-testid="client-search"
              />
            </div>
            
            {/* Client List */}
            <ScrollArea className="h-[300px] border border-white/10 rounded-lg">
              {loadingMappedClients ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                </div>
              ) : mappedClients.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No clients found with phone numbers</p>
                </div>
              ) : (
                mappedClients.map((client) => (
                  <div
                    key={client.client_code}
                    onClick={() => {
                      // Create a chat object and select it
                      const chatObj = {
                        phone: client.phone,
                        name: client.name,
                        client: client
                      };
                      setSelectedChat(chatObj);
                      setMessages([]);
                      setShowNewChatDialog(false);
                    }}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/5 border-b border-white/5 transition-colors"
                    data-testid={`client-${client.client_code}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{client.name}</p>
                      <p className="text-sm text-slate-400 flex items-center gap-2">
                        <Phone className="w-3 h-3" />
                        {client.phone}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-[#C5A059]">{client.client_code}</span>
                        {client.tier && (
                          <>
                            <span className="text-xs text-slate-500">•</span>
                            <Badge variant="outline" className="text-xs border-white/20">
                              {client.tier}
                            </Badge>
                          </>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </div>
                ))
              )}
            </ScrollArea>
            
            <p className="text-xs text-slate-400 text-center">
              Showing {mappedClients.length} clients with phone numbers
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewChatDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppPage;
